/**
 * wishlistRowToSkuCardData — MS64a adapter.
 *
 * The `wishlists` table joins back to a narrow subset of
 * vendor_products fields (id, name, slug, description, price,
 * image_url, is_voucher, vendor_category, vendor_name) for card
 * rendering on /wishlist + the dashboard saved-gifts cluster.
 *
 * Both surfaces want to upgrade from the legacy "deep-link into
 * /chat with a brief" CTA to the MS61c ProductDetailModal +
 * OrderConfirmSheet flow that the rest of the catalogue already
 * uses. Both expect a SkuCardData shape (from
 * @/components/chat/SkuCard).
 *
 * This function bridges the two shapes — narrow WishlistRow.sku
 * to the SkuCardData expected by ProductDetailModal. The modal's
 * useSkuDetail hook then lazy-fetches the FULL row (with
 * long_description / gallery / voucher_terms / variants) on open,
 * so the lean WishlistRow.sku data is only used for the initial
 * paint.
 *
 * highlight_fields is left as empty — the wishlist context doesn't
 * carry the LLM-chosen highlight metadata (which only the chat
 * carousel produces). The modal renders without them; OK.
 *
 * why_this_one is similarly omitted — there's no chat rationale
 * tied to a saved gift, so the modal's "Why we picked this"
 * editorial frame stays hidden.
 */

import type { SkuCardData } from "@/components/chat/SkuCard";
import type { WishlistRow } from "@/hooks/queries/useWishlist";

/** Returns null when the row's sku has been deleted/unavailable. */
export function wishlistRowToSkuCardData(
  row: WishlistRow,
): SkuCardData | null {
  const sku = row.sku;
  if (!sku) return null;
  return {
    sku_id: sku.id,
    name: sku.name,
    vendor_name: sku.vendor_name,
    vendor_category: sku.vendor_category,
    price_aud: Number(sku.price ?? 0),
    image_url: sku.image_url,
    slug: sku.slug ?? "",
    description: sku.description,
    is_voucher: Boolean(sku.is_voucher),
    // MS327: propagate lead_time_days + dietary_tags so the
    // OrderConfirmSheet MS312 lead-time gate and MS318 recipient-
    // conflict warning fire on saved-gift clicks too. useWishlist
    // SELECTs both as of MS327.
    lead_time_days: sku.lead_time_days,
    dietary_tags: sku.dietary_tags,
    // Wishlist context has no chat-rationale or highlight chips —
    // those only come from the chat carousel where the model
    // chose them. Leave empty.
    why_this_one: undefined,
    highlight_fields: [],
  };
}
